/**
 * §9 Layer 3 — the performance-decay auto-stop. The key safeguard.
 *
 * "Patients under-report. They are motivated, they want to do well, and they
 * have been taught that stopping is failure. But performance degrades
 * measurably before the patient notices symptoms."
 *
 * This is what watches for that. It learns what the first stretch of an
 * exercise looks like FOR THIS PATIENT ON THIS DAY, then compares what is
 * happening now against it. When the gap is wide enough, the exercise stops
 * itself — and the wording around that stop treats it as normal and expected,
 * never as an error (§9 Layer 6, in `copy.ts`).
 *
 * IT CONTAINS NO EXERCISE-SPECIFIC CODE, and that is the whole design. Each
 * exercise declares its own decay signature — which of its metrics reveal
 * fatigue and in which direction — and this reads that. It has never heard of
 * smooth pursuit. A new exercise is monitored the day it is registered, which
 * is the §1 acceptance test applied to the safety system.
 *
 * WHY A WITHIN-SESSION BASELINE rather than the patient's history. Metabolic
 * capacity is not a fixed quantity: the same person has a different ceiling on
 * a bad week, after a bad night, at the end of a working day. A baseline taken
 * from last month would flag a slow day as decay and miss a real collapse that
 * started from an unusually good opening. The comparison that means something
 * is against the opening of this run.
 *
 * WHAT IT IS NOT. Not an assessment, not a score, and never shown to the
 * patient as a number (constraints 3 and 6). The only thing a patient ever sees
 * is the exercise ending and being told that was the right outcome.
 */

import {
  METRIC_ACCUMULATION,
  type DecaySignal,
  type MetricKey,
  type MetricSample,
} from '@pathways/exercises';
import type { RunnerSafety } from '../../api.js';

/**
 * How long the monitor spends learning before it will stop anything (§9: "a
 * within-session rolling baseline over the first 20-30 seconds").
 *
 * The dose-reduction floor in `packages/exercises/src/dose.ts` is 30 seconds
 * and these two numbers are related on purpose: an exercise reduced to the
 * floor still has ten seconds of watching after the baseline closes. Change one
 * and look at the other.
 */
export const BASELINE_WINDOW_MS = 20_000;

/**
 * How much of the recent past "now" means. Long enough that one slow trial
 * cannot end a session, short enough that a real collapse is caught while it is
 * still a collapse rather than an average.
 */
export const RECENT_WINDOW_MS = 15_000;

/** Below these, there is not enough to compare and the monitor says nothing. */
export const MIN_BASELINE_SAMPLES = 5;
export const MIN_RECENT_SAMPLES = 3;

/**
 * How many consecutive readings must exceed the threshold before the exercise
 * stops.
 *
 * Two, not one. The rolling means already smooth a single bad trial, and this
 * guards the case they do not: a sample that arrives at the exact moment the
 * window turns over. The cost of the extra reading is a second or two of
 * therapy; the cost of a false stop is a patient learning that the app takes
 * their session away, which is how they stop reporting honestly.
 */
export const CONSECUTIVE_READINGS_TO_STOP = 2;

/**
 * Sensitivity 0-100 to the proportional drop that stops an exercise.
 *
 * The doctor's form offers three points — 25, 50 and 75 — which land at 27.5%,
 * 20% and 12.5%. NONE OF THESE NUMBERS IS VALIDATED. They cannot be until real
 * patients have generated real sessions; that is a standing open question, and
 * it is why every reduction the monitor causes is reversible in one click.
 */
export const DECAY_THRESHOLD_AT_ZERO = 0.35;
export const DECAY_THRESHOLD_AT_MAX = 0.05;

export function decayThreshold(sensitivity: number): number {
  const clamped = Math.min(100, Math.max(0, sensitivity));
  return (
    DECAY_THRESHOLD_AT_ZERO -
    (clamped / 100) * (DECAY_THRESHOLD_AT_ZERO - DECAY_THRESHOLD_AT_MAX)
  );
}

/** What the monitor saw, kept whole so the doctor can read what happened. */
export interface DecayTrace {
  /** Per signal: the baseline value, the recent value, and the drop as a ratio. */
  signals: ReadonlyArray<{
    metric: MetricKey;
    direction: DecaySignal['direction'];
    weight: number;
    baseline: number;
    recent: number;
    /** Positive means worse. Negative means they improved. */
    degradation: number;
  }>;
  /** The weighted degradation across every signal. */
  overall: number;
  threshold: number;
  atMs: number;
}

export type DecayReading =
  | { kind: 'learning' }
  | { kind: 'watching'; trace: DecayTrace }
  | { kind: 'stop'; trace: DecayTrace };

/**
 * One exercise's monitor. Fed every sample the exercise emits, in order.
 *
 * Deliberately a plain object rather than a hook or a component: it is called
 * from inside a render-free callback on every trial, and the thing it must
 * never do is cause a re-render while an exercise is drawing frames.
 */
export class DecayMonitor {
  private readonly samples: MetricSample[] = [];
  private readonly weights: Map<MetricKey, { weight: number; direction: DecaySignal['direction'] }>;
  private readonly threshold: number;
  private consecutive = 0;
  private stopped = false;

  constructor(
    decay: readonly DecaySignal[],
    safety: Pick<RunnerSafety, 'decaySensitivity'>,
  ) {
    this.threshold = decayThreshold(safety.decaySensitivity);
    this.weights = new Map(
      decay.map((signal) => [
        signal.metric,
        { weight: signal.weight ?? 1, direction: signal.direction },
      ]),
    );
  }

  /**
   * Offer a sample and get back what the monitor now thinks.
   *
   * Returns 'stop' at most once. After that the caller has stopped the exercise
   * and any further samples are the tail of a run loop that has not yet noticed.
   */
  observe(sample: MetricSample): DecayReading {
    if (this.stopped) return { kind: 'learning' };
    if (!this.weights.has(sample.metric)) return this.read(sample.t);
    if (!Number.isFinite(sample.value)) return this.read(sample.t);
    this.samples.push(sample);
    return this.read(sample.t);
  }

  private read(nowMs: number): DecayReading {
    if (nowMs < BASELINE_WINDOW_MS) return { kind: 'learning' };

    const signals: Array<DecayTrace['signals'][number]> = [];
    let weighted = 0;
    let totalWeight = 0;

    for (const [metric, { weight, direction }] of this.weights) {
      const baseline = this.summarise(
        metric,
        (s) => s.t < BASELINE_WINDOW_MS,
        MIN_BASELINE_SAMPLES,
      );
      const recent = this.summarise(
        metric,
        (s) => s.t >= nowMs - RECENT_WINDOW_MS,
        MIN_RECENT_SAMPLES,
      );
      if (baseline === null || recent === null) continue;

      // A baseline of zero has no proportional change to measure — a perfect
      // opening on a metric where zero is the good end. Skipped rather than
      // divided by, which would be an infinity that stopped every session.
      if (baseline === 0) continue;

      const change = (recent - baseline) / Math.abs(baseline);
      const degradation = direction === 'increase' ? change : -change;

      signals.push({ metric, direction, weight, baseline, recent, degradation });
      weighted += degradation * weight;
      totalWeight += weight;
    }

    if (totalWeight === 0) return { kind: 'learning' };

    const overall = weighted / totalWeight;
    const trace: DecayTrace = { signals, overall, threshold: this.threshold, atMs: nowMs };

    if (overall >= this.threshold) {
      this.consecutive += 1;
      if (this.consecutive >= CONSECUTIVE_READINGS_TO_STOP) {
        this.stopped = true;
        return { kind: 'stop', trace };
      }
    } else {
      // A single reading back under the threshold clears the count. Fatigue
      // that is real does not come and go between two trials, and treating it
      // as though it might would make the monitor fire on noise.
      this.consecutive = 0;
    }

    return { kind: 'watching', trace };
  }

  /**
   * One number for a metric over a window.
   *
   * The rule depends on what the metric IS, which the contract already states.
   * A per-trial metric is one observation per trial and averages. A cumulative
   * metric is a running total that supersedes itself — accuracy is emitted as
   * correct-over-answered so far — so the last value in the window is the value,
   * and averaging them would weight the opening trials many times over.
   *
   * A cumulative metric is heavily damped by its own nature: a run that was
   * perfect for a minute cannot show a 20% fall in its total in ten seconds.
   * That is a property of the metric and not a fault here. The monitor rides
   * mainly on the per-trial signals, which is why every exercise in the library
   * declares at least one.
   */
  private summarise(
    metric: MetricKey,
    inWindow: (s: MetricSample) => boolean,
    minimum: number,
  ): number | null {
    const window = this.samples.filter((s) => s.metric === metric && inWindow(s));
    if (window.length < minimum) return null;

    if (METRIC_ACCUMULATION[metric] === 'cumulative') {
      return window[window.length - 1]!.value;
    }
    return window.reduce((sum, s) => sum + s.value, 0) / window.length;
  }
}
